import type { V2DownlinePositionVM } from '~/composables/useRequest/requests/data-contracts';


const CACHE_KEY_UPLINE = 'account-upline-list';
const CACHE_KEY_PARAMS = 'account-upline-params';
const CACHE_KEY_LOADING = 'account-upline-loading';
const CACHE_KEY_PROMISE = 'account-upline-loadPromise';
const CACHE_KEY_LOADED_AT = 'account-upline-loadedAt';
const CACHE_KEY_TTL_TIMER = 'account-upline-ttlTimer';
const CACHE_TTL_MS = 60_000;

type UplineParams = {
  treeId: number;
  offset: number;
};

/**
 * Общий кэш upline текущей позиции аккаунта.
 * ensureLoaded всегда возвращает копию — clear() не затрагивает данные в компонентах.
 */
export default function accountUplineData() {
  const upline = useState<V2DownlinePositionVM[]>(CACHE_KEY_UPLINE, () => []);
  const cachedParams = useState<string | null>(CACHE_KEY_PARAMS, () => null);
  const isLoading = useState<boolean>(CACHE_KEY_LOADING, () => false);
  const loadPromise = useState<Promise<V2DownlinePositionVM[]> | null>(CACHE_KEY_PROMISE, () => null);
  const loadedAt = useState<number | null>(CACHE_KEY_LOADED_AT, () => null);
  const ttlTimer = useState<ReturnType<typeof setTimeout> | null>(CACHE_KEY_TTL_TIMER, () => null);

  const buildCacheKey = (accountId: number, params: UplineParams) =>
    `${accountId}:${params.treeId}:${params.offset}`;

  const clone = (list: V2DownlinePositionVM[]) => list.slice();

  const clearTtlTimer = () => {
    if (ttlTimer.value != null) {
      clearTimeout(ttlTimer.value);
      ttlTimer.value = null;
    }
  };

  const clear = () => {
    clearTtlTimer();
    upline.value = [];
    cachedParams.value = null;
    loadedAt.value = null;
    loadPromise.value = null;
    isLoading.value = false;
  };

  const scheduleClear = () => {
    clearTtlTimer();

    ttlTimer.value = setTimeout(() => {
      clear();
    }, CACHE_TTL_MS);
  };

  const isFresh = (key: string) =>
    cachedParams.value === key &&
    loadedAt.value != null &&
    Date.now() - loadedAt.value < CACHE_TTL_MS;

  const ensureLoaded = (
    accountId: number,
    params: UplineParams,
    mode: 'soft' | 'hard' = 'soft',
  ): Promise<V2DownlinePositionVM[]> => {
    const key = buildCacheKey(accountId, params);

    if (mode === 'soft' && loadPromise.value && cachedParams.value === key) {
      if (isLoading.value || isFresh(key)) {
        return loadPromise.value.then(clone);
      }

      clear();
    }

    if (mode === 'hard' || cachedParams.value !== key) {
      clear();
    }

    cachedParams.value = key;

    const promise = (async () => {
      try {
        isLoading.value = true;

        const list = await getAllUpline(accountId, {
          treeId: params.treeId,
          offset: params.offset,
        });

        if (cachedParams.value !== key) {
          return clone(list);
        }

        upline.value = clone(list);
        loadedAt.value = Date.now();
        scheduleClear();

        return clone(list);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch account upline:', error);

        // Do not cache failures — next soft call must retry
        if (cachedParams.value === key) {
          clear();
        }

        return [];
      } finally {
        if (cachedParams.value === key) {
          isLoading.value = false;
        }
      }
    })();

    loadPromise.value = promise;

    return promise.then(clone);
  };

  return {
    isLoading,
    ensureLoaded,
    clear,
  };
}
